// presto-qr-pairing (ADR 2026-09-03): the browser half of pairing a display.
//
// A screen shows a QR (and the same code in large type). Someone who can SEE it either
// photographs it here or types it. This module is the logic behind both, kept out of the
// component so it can be tested without a camera.
//
// Why a PHOTO and not a live `getUserMedia` scanner: getUserMedia requires a secure
// context, so a phone hitting `http://<lan-ip>:8080` in dev cannot use it — and a
// pairing flow you can't test in dev is not a flow. `<input capture>` hands off to the
// camera app instead: no permission prompt, no secure-context requirement, and it is
// literally "take a pic of the QR code".

// Crockford base32 — the agent mints from this alphabet precisely because I, L, O and U
// are absent, so nothing is ambiguous read off a screen across a room.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

// A 12MP capture is ~4000x3000. jsQR over that ImageData is seconds of main-thread work
// and a memory spike mobile Safari may kill the tab for. The QR only needs to survive
// downscaling to something a camera could have shot in the first place.
export const MAX_DECODE_EDGE = 1000;

/**
 * Can this station be paired to a screen? Mirrors the agent's `_secondary_display`,
 * which is the authority — the button must never offer a pairing the backend refuses,
 * and (just as important) must not hide one it would accept.
 *
 * `presto_enabled` is the LEGACY alias, still honoured server-side. Checking only the
 * new flag hides the button on exactly the stations that already have a screen: 3 of
 * dev's 4 opted-in stations carry only the old name.
 */
export function canPair(stationConfig) {
  const cfg = stationConfig || {};
  return Boolean(cfg.secondary_display || cfg.presto_enabled);
}

/**
 * Fold what a human typed into the canonical code — Crockford's decoding rules, which
 * is the whole reason the alphabet excludes those letters: `I`/`L` read as `1`, `O`
 * reads as `0`, case is irrelevant, separators are noise. Must agree with the agent's
 * `normalize_code`; someone squinting at a wall screen should not lose a pairing to a
 * 0-versus-O call.
 *
 * Returns '' for anything that could not be a code.
 */
export function normalizeCode(raw) {
  if (typeof raw !== 'string') return '';
  let out = '';
  for (const ch of raw.trim().toUpperCase()) {
    if (ch === '-' || ch === ' ' || ch === '\t' || ch === '_') continue;
    let c = ch;
    if (c === 'I' || c === 'L') c = '1';
    else if (c === 'O') c = '0';
    else if (c === 'U') return '';        // not in the alphabet; never silently map it
    if (!ALPHABET.includes(c)) return '';
    out += c;
  }
  return out;
}

/**
 * Pull the code out of whatever the QR held. The QR encodes a URL
 * (`https://<origin>/pair/<code>`) rather than a bare code so that a phone's NATIVE
 * camera also works — that is the rescue path for every browser where in-app decoding
 * fails, and it costs nothing here.
 */
export function codeFromScan(text) {
  if (typeof text !== 'string') return '';
  const trimmed = text.trim();
  const marker = '/pair/';
  const at = trimmed.lastIndexOf(marker);
  const tail = at >= 0 ? trimmed.slice(at + marker.length) : trimmed;
  // Stop at a query string / fragment / trailing slash.
  return normalizeCode(tail.split(/[?#/]/)[0]);
}

/** Scale (w, h) down to fit `max` on the long edge, preserving aspect. Never scales up. */
export function fitWithin(w, h, max = MAX_DECODE_EDGE) {
  const long = Math.max(w, h);
  if (!long || long <= max) return { w, h };
  const k = max / long;
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/**
 * Decode a QR out of a photo the user just took.
 *
 * Hands the file to the BROWSER's own image decoder (`createImageBitmap`) rather than
 * parsing bytes: iOS sometimes returns HEIC, which Safari decodes natively and a JS QR
 * library does not, so decoding through the platform makes the format question vanish.
 * Then `BarcodeDetector` where it exists (Chromium), else jsQR.
 *
 * Returns the code, or '' if nothing readable was in the picture.
 */
export async function decodeImageFile(file) {
  if (!file) return '';
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (_) {
    return '';
  }
  const { w, h } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);

  if (typeof window !== 'undefined' && window.BarcodeDetector) {
    try {
      const det = new window.BarcodeDetector({ formats: ['qr_code'] });
      const found = await det.detect(canvas);
      if (found && found.length) return codeFromScan(found[0].rawValue);
    } catch (_) {
      // fall through to jsQR — a detector that exists but refuses this input is not
      // a reason to give up on the photo.
    }
  }
  try {
    const jsQR = (await import('jsqr')).default;
    const data = ctx.getImageData(0, 0, w, h);
    const res = jsQR(data.data, w, h);
    return res ? codeFromScan(res.data) : '';
  } catch (_) {
    return '';
  }
}

/**
 * Redeem a code against a station. This only CLAIMS the pairing — the agent holds it
 * pending until someone taps the screen itself (D7), which is why the happy path here
 * says "confirm on the screen" rather than "done".
 */
export async function submitPairing(code, stationId) {
  const res = await fetch('/display/pair', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, station_id: stationId }),
  });
  let body = {};
  try { body = await res.json(); } catch (_) { /* error responses may carry no body */ }
  if (!res.ok) {
    // aiohttp puts the useful sentence in the reason phrase; surface it verbatim —
    // "this screen's credential is pinned to a single station" tells the operator
    // exactly what to do, and inventing our own copy would lose that.
    const err = new Error(body.error || res.statusText || 'pairing failed');
    err.status = res.status;
    throw err;
  }
  return body;
}
